import { useEffect, useState } from "react";
import { Main } from "@/components/Main";
import { PageHeader } from "@/components/PageHeader";
import { CreateLoaderTable } from "@/components/Loaders";
import { cn } from "@/lib/utils";
import { colors } from "@/utils/colors";
import {
  getMyTimeEntriesData,
  getHourBank,
} from "@/Api/timeEntries";
import {
  getMyAdjustmentRequests,
  getMyPendingApprovals,
} from "@/Api/timeEntryAdjustmentRequests";
import {
  getMyTimeEntryMirrorReports,
  getMyTimeEntryMirrorReportDownloadUrl,
} from "@/Api/timeEntryMirror";
import { TimeEntryTypeLabel } from "@/types/timeEntry";
import {
  TimeEntryAdjustmentStatusConst,
  TimeEntryAdjustmentStatusLabel,
} from "@/types/timeEntryAdjustmentRequest";
import {
  TimeEntryMirrorSignatureStatusConst,
  TimeEntryMirrorSignatureStatusLabel,
} from "@/types/timeEntryMirror";
import { ShowTimeEntryBtn } from "@/App/TimeEntries/Show";
import { RequestAdjustmentBtn } from "@/App/TimeEntryAdjustmentRequests/Request";
import { DecideAdjustmentRequestBtns } from "@/App/TimeEntryAdjustmentRequests/Decide";
import { SignReportBtn } from "@/App/TimeEntryMirror/Sign";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import {
  MoreHorizontal,
  Fingerprint,
  Scale,
  AlertTriangle,
  Download,
  FileSignature,
} from "lucide-react";

type HourBank = {
  period_start: string | null;
  period_end: string | null;
  expected_seconds: number | null;
  worked_seconds: number | null;
  abonoed_seconds: number | null;
  balance_seconds: number | null;
};

type EntryRow = {
  id: number;
  uuid: string;
  time_entry_type_id: number;
  clocked_at: string;
};

type DayGroup = {
  date: string;
  worked_seconds: number;
  entries: EntryRow[];
};

const formatHours = (totalSeconds: number): string => {
  const sign = totalSeconds < 0 ? "-" : "";
  const seconds = Math.abs(Math.floor(totalSeconds));
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  return `${sign}${hours}h${String(minutes).padStart(2, "0")}min`;
};

const formatDayLabel = (dateYmd: string): string => {
  const [year, month, day] = dateYmd.split("-").map(Number);
  const date = new Date(year, month - 1, day);
  const weekday = date.toLocaleDateString("pt-BR", { weekday: "long" });
  return `${String(day).padStart(2, "0")}/${String(month).padStart(2, "0")}/${year} — ${weekday}`;
};

const HourBankCard = ({ bank }: { bank: HourBank | null }) => {
  if (!bank || bank.expected_seconds === null) {
    return (
      <div className="rounded-md border p-4 bg-zinc-50 dark:bg-[#2c2c2c] text-sm text-muted-foreground">
        Banco de horas indisponível — configure um Horário de Trabalho no seu
        cadastro para acompanhar aqui.
      </div>
    );
  }

  const balance = bank.balance_seconds ?? 0;
  const isPositive = balance >= 0;

  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
      <div className="rounded-md border p-3 bg-zinc-50 dark:bg-[#2c2c2c]">
        <div className="text-xs text-muted-foreground">
          Horas previstas (desde {bank.period_start})
        </div>
        <div className="font-semibold text-lg">
          {formatHours(bank.expected_seconds ?? 0)}
        </div>
      </div>
      <div className="rounded-md border p-3 bg-zinc-50 dark:bg-[#2c2c2c]">
        <div className="text-xs text-muted-foreground">Horas cumpridas</div>
        <div className="font-semibold text-lg">
          {formatHours(bank.worked_seconds ?? 0)}
        </div>
        {!!bank.abonoed_seconds && (
          <div className="text-xs text-muted-foreground">
            inclui {formatHours(bank.abonoed_seconds)} abonadas
          </div>
        )}
      </div>
      <div
        className={cn(
          "rounded-md border p-3",
          isPositive
            ? "bg-green-500/10 border-green-500/30"
            : "bg-destructive/10 border-destructive/30"
        )}
      >
        <div className="text-xs text-muted-foreground">Saldo do mês</div>
        <div
          className={cn(
            "font-semibold text-lg",
            isPositive ? "text-green-600 dark:text-green-400" : "text-destructive"
          )}
        >
          {isPositive ? "+" : ""}
          {formatHours(balance)}
        </div>
      </div>
    </div>
  );
};

export const PageList = () => {
  const [bank, setBank] = useState<HourBank | null>(null);
  const [listData, setListData] = useState<any>({});
  const [state, setState] = useState("loading");
  const [pendingApprovals, setPendingApprovals] = useState<any[]>([]);
  const [myRequests, setMyRequests] = useState<any>({});
  const [mirrorReports, setMirrorReports] = useState<any>({});
  const [downloadingReport, setDownloadingReport] = useState<number | null>(null);

  const fetchExtras = async () => {
    const [pendingResponse, requestsResponse, mirrorResponse] = await Promise.all([
      getMyPendingApprovals(),
      getMyAdjustmentRequests(),
      getMyTimeEntryMirrorReports(),
    ]);
    setPendingApprovals(pendingResponse.data?.response?.data ?? []);
    setMyRequests(requestsResponse.data?.response ?? {});
    setMirrorReports(mirrorResponse.data?.response ?? {});
  };

  const handleDownloadMirrorReport = async (employeeReportUuid: string, id: number) => {
    setDownloadingReport(id);
    const { data, ok } = await getMyTimeEntryMirrorReportDownloadUrl(employeeReportUuid);
    setDownloadingReport(null);
    if (!ok || !data?.response?.url) return;
    window.open(data.response.url, "_blank");
  };

  const mirrorPeriodLabel = (report: any) =>
    `${new Date(report.request?.date_start).toLocaleDateString("pt-BR", { timeZone: "UTC" })} a ${new Date(report.request?.date_end).toLocaleDateString("pt-BR", { timeZone: "UTC" })}`;

  const pendingMirrorReports = (mirrorReports?.data ?? []).filter(
    (report: any) => report.signature_status === TimeEntryMirrorSignatureStatusConst.PENDING
  );

  useEffect(() => {
    (async () => {
      const [bankResponse, entriesResponse] = await Promise.all([
        getHourBank(),
        getMyTimeEntriesData(1),
      ]);
      setBank(bankResponse.data?.response ?? null);
      setListData(entriesResponse.data?.response ?? {});
      await fetchExtras();
      setState("production");
    })();
  }, []);

  const pageActions = {
    next: async () => {
      if (listData?.current_page < listData?.last_page) {
        setState("loading");
        const { data } = await getMyTimeEntriesData(listData.current_page + 1);
        setListData(data.response);
        setState("production");
      }
    },
    previous: async () => {
      if (listData?.current_page > 1) {
        setState("loading");
        const { data } = await getMyTimeEntriesData(listData.current_page - 1);
        setListData(data.response);
        setState("production");
      }
    },
    lastPage: async () => {
      if (listData?.current_page !== listData?.last_page) {
        setState("loading");
        const { data } = await getMyTimeEntriesData(listData.last_page);
        setListData(data.response);
        setState("production");
      }
    },
  };

  return (
    <Main>
      <PageHeader
        icon={Fingerprint}
        title="Meus Registros de Ponto"
        description="Acompanhe suas batidas e o saldo do seu banco de horas"
      />

      {pendingApprovals.length > 0 && (
        <div className="flex flex-col gap-2 mb-4 rounded-md border border-amber-500/40 bg-amber-500/10 p-4">
          <label className="text-sm font-medium flex items-center gap-1 text-amber-600 dark:text-amber-400">
            <AlertTriangle className="size-4" /> Aguardando sua aprovação
          </label>
          {pendingApprovals.map((item: any) => (
            <div
              key={item.uuid}
              className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 rounded-md border p-3 bg-zinc-50 dark:bg-[#2c2c2c]"
            >
              <div className="text-sm">
                <div className="font-semibold">
                  {TimeEntryTypeLabel[item.time_entry_type_id]} em{" "}
                  {new Date(item.requested_date).toLocaleDateString("pt-BR", { timeZone: "UTC" })}{" "}
                  às {item.requested_time}
                </div>
                <div className="text-muted-foreground">
                  Solicitado por {item.requested_by?.name}: {item.justification}
                </div>
              </div>
              <DecideAdjustmentRequestBtns
                uuid={item.uuid}
                onDecided={fetchExtras}
              />
            </div>
          ))}
        </div>
      )}

      {pendingMirrorReports.length > 0 && (
        <div className="flex flex-col gap-2 mb-4 rounded-md border border-amber-500/40 bg-amber-500/10 p-4">
          <label className="text-sm font-medium flex items-center gap-1 text-amber-600 dark:text-amber-400">
            <FileSignature className="size-4" /> Espelho de ponto aguardando sua assinatura
          </label>
          {pendingMirrorReports.map((report: any) => (
            <div
              key={report.uuid}
              className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 rounded-md border p-3 bg-zinc-50 dark:bg-[#2c2c2c]"
            >
              <div className="text-sm font-semibold">
                Período: {mirrorPeriodLabel(report)}
              </div>
              <div className="flex gap-2">
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  disabled={downloadingReport === report.id}
                  onClick={() => handleDownloadMirrorReport(report.uuid, report.id)}
                >
                  <Download className="size-4" />
                  Baixar
                </Button>
                <SignReportBtn
                  employeeReportUuid={report.uuid}
                  periodLabel={mirrorPeriodLabel(report)}
                  onSigned={fetchExtras}
                />
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="flex flex-col gap-2 mb-4">
        <label className="text-sm font-medium flex items-center gap-1">
          <Scale className="size-4" /> Banco de horas (mês atual)
        </label>
        <HourBankCard bank={bank} />
      </div>

      {state === "loading" ? (
        <CreateLoaderTable cols={4} lines={6} />
      ) : (
        <>
          {listData?.data?.length > 0 ? (
            <Accordion
              type="single"
              collapsible
              className="rounded-md border bg-zinc-50 dark:bg-[#2c2c2c]"
            >
              {listData.data.map((day: DayGroup) => (
                <AccordionItem key={day.date} value={day.date} className="px-4">
                  <AccordionTrigger className="hover:no-underline">
                    <div className="flex flex-1 items-center justify-between pr-2">
                      <div className="flex items-center gap-3">
                        <span className="font-medium">
                          {formatDayLabel(day.date)}
                        </span>
                        <Badge variant="outline">
                          {day.entries.length}{" "}
                          {day.entries.length === 1 ? "batida" : "batidas"}
                        </Badge>
                      </div>
                      <div
                        className={cn(
                          "flex items-center gap-1 text-sm",
                          day.worked_seconds > 0
                            ? "text-muted-foreground"
                            : "text-amber-500"
                        )}
                      >
                        {day.worked_seconds === 0 && (
                          <AlertTriangle className="size-4" />
                        )}
                        {formatHours(day.worked_seconds)} trabalhadas
                      </div>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Tipo</TableHead>
                          <TableHead>Horário da Batida</TableHead>
                          <TableHead className="text-center">Ações</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {day.entries.map((item: EntryRow) => (
                          <TableRow key={item.id}>
                            <TableCell>
                              <Badge variant="outline">
                                {TimeEntryTypeLabel[item.time_entry_type_id]}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              {new Date(item.clocked_at).toLocaleString(
                                "pt-BR"
                              )}
                            </TableCell>
                            <TableCell className="flex justify-center gap-1">
                              <ShowTimeEntryBtn uuid={item.uuid} />
                              <RequestAdjustmentBtn
                                entry={item}
                                onCreated={fetchExtras}
                              />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          ) : (
            <div className="rounded-md border bg-zinc-50 dark:bg-[#2c2c2c] p-4 text-sm text-muted-foreground">
              Nada foi encontrado.
            </div>
          )}
          <div className="flex justify-between mb-3 mt-2">
            {listData?.data?.length > 0 && (
              <span className="font-medium">
                Exibindo {listData?.current_page} de um total de{" "}
                {listData?.last_page}
              </span>
            )}
            {listData?.last_page > 1 && (
              <Pagination>
                <PaginationContent>
                  <PaginationItem>
                    <PaginationPrevious
                      href="#"
                      className={cn("h-8", colors.bg_primary)}
                      onClick={pageActions.previous}
                    />
                  </PaginationItem>
                  <PaginationItem>
                    <PaginationLink
                      href="#"
                      className="size-8 bg-transparent border-2 border-[#023859] text-slate-900 font-bold cursor-auto hover:bg-transparent"
                    >
                      <span className="pointer-events-none dark:text-[#F4F4F5]">
                        {listData?.current_page}
                      </span>
                    </PaginationLink>
                  </PaginationItem>
                  <PaginationItem>
                    <PaginationLink
                      href="#"
                      className="text-black h-8 hover:bg-stone-950/10"
                      onClick={pageActions.lastPage}
                    >
                      <MoreHorizontal className="h-4 w-4 dark:text-[#F4F4F5]" />
                    </PaginationLink>
                  </PaginationItem>
                  <PaginationItem>
                    <PaginationNext
                      href="#"
                      className={cn("h-8", colors.bg_primary)}
                      onClick={pageActions.next}
                    />
                  </PaginationItem>
                </PaginationContent>
              </Pagination>
            )}
          </div>

          {myRequests?.data?.length > 0 && (
            <div className="flex flex-col gap-2 mt-6">
              <label className="text-sm font-medium">
                Minhas solicitações de ajuste
              </label>
              <Table className="bg-zinc-50 rounded dark:bg-[#2c2c2c]">
                <TableHeader>
                  <TableRow>
                    <TableHead>Tipo</TableHead>
                    <TableHead>Data/Horário solicitado</TableHead>
                    <TableHead>Justificativa</TableHead>
                    <TableHead className="text-center">Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {myRequests.data.map((item: any) => (
                    <TableRow key={item.uuid}>
                      <TableCell>
                        {TimeEntryTypeLabel[item.time_entry_type_id]}
                      </TableCell>
                      <TableCell>
                        {new Date(item.requested_date).toLocaleDateString(
                          "pt-BR",
                          { timeZone: "UTC" }
                        )}{" "}
                        às {item.requested_time}
                      </TableCell>
                      <TableCell className="truncate max-w-80">
                        {item.justification}
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge
                          variant={
                            item.status === TimeEntryAdjustmentStatusConst.APPROVED
                              ? "default"
                              : item.status ===
                                  TimeEntryAdjustmentStatusConst.REJECTED
                                ? "destructive"
                                : "outline"
                          }
                        >
                          {TimeEntryAdjustmentStatusLabel[item.status]}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}

          {mirrorReports?.data?.length > 0 && (
            <div className="flex flex-col gap-2 mt-6">
              <label className="text-sm font-medium">
                Meus espelhos de ponto
              </label>
              <Table className="bg-zinc-50 rounded dark:bg-[#2c2c2c]">
                <TableHeader>
                  <TableRow>
                    <TableHead>Período</TableHead>
                    <TableHead className="text-center">Assinatura</TableHead>
                    <TableHead className="text-center">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {mirrorReports.data.map((report: any) => (
                    <TableRow key={report.uuid}>
                      <TableCell>{mirrorPeriodLabel(report)}</TableCell>
                      <TableCell className="text-center">
                        <Badge
                          variant={
                            report.signature_status ===
                            TimeEntryMirrorSignatureStatusConst.SIGNED
                              ? "default"
                              : "outline"
                          }
                        >
                          {TimeEntryMirrorSignatureStatusLabel[report.signature_status]}
                        </Badge>
                      </TableCell>
                      <TableCell className="flex justify-center gap-2">
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          className="size-7 p-0 hover:bg-stone-950/10"
                          title="Baixar PDF"
                          disabled={downloadingReport === report.id}
                          onClick={() => handleDownloadMirrorReport(report.uuid, report.id)}
                        >
                          <Download className="size-5 text-blue-600" />
                        </Button>
                        {report.signature_status ===
                          TimeEntryMirrorSignatureStatusConst.PENDING && (
                          <SignReportBtn
                            employeeReportUuid={report.uuid}
                            periodLabel={mirrorPeriodLabel(report)}
                            onSigned={fetchExtras}
                          />
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </>
      )}
    </Main>
  );
};

export default PageList;
